import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import MapEditor from '../components/MapEditor'
import PinGate from '../components/PinGate'
import SpotPropsForm, { type SpotProps } from '../components/SpotPropsForm'
import { api, foutTekst } from '../lib/api'
import type { AdminRegistration, MapMeta, PublicSpot } from '../lib/types'
import PENDING_SPOTS from '../data/pendingSpots.json'

export default function Plekken() {
  return (
    <PinGate>
      {(token, logout) => <Inner token={token} onLogout={logout} />}
    </PinGate>
  )
}

type Mode =
  | { kind: 'view' }
  | { kind: 'draw'; vertices: [number, number][] }
  | { kind: 'edit'; spotId: string; vertices: [number, number][] }

function Inner({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [spots, setSpots] = useState<PublicSpot[] | null>(null)
  const [meta, setMeta] = useState<MapMeta | null>(null)
  const [registrations, setRegistrations] = useState<AdminRegistration[] | null>(null)
  const [mode, setMode] = useState<Mode>({ kind: 'view' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  // Props pre-filled when drawing a spot picked from the "nog te tekenen" list.
  const [drawPrefill, setDrawPrefill] = useState<SpotProps | null>(null)

  async function load() {
    const [spotsRes, regsRes] = await Promise.all([
      api<{ meta: MapMeta; spots: PublicSpot[] }>('/api/spots'),
      api<{ registrations: AdminRegistration[] }>('/api/admin/registrations', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
    if (!spotsRes.ok) { setError('Plekken konden niet geladen worden.'); return }
    if (!regsRes.ok) {
      if (regsRes.status === 401) onLogout()
      setError('Inschrijvingen konden niet geladen worden.')
      return
    }
    setError(null)
    setSpots(spotsRes.data.spots)
    setMeta(spotsRes.data.meta)
    setRegistrations(regsRes.data.registrations)
  }

  useEffect(() => { load() }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  const claims = useMemo(() => {
    const m = new Map<string, AdminRegistration[]>()
    for (const r of registrations ?? []) {
      for (const id of r.spots) {
        if (!m.has(id)) m.set(id, [])
        m.get(id)!.push(r)
      }
    }
    return m
  }, [registrations])

  if (error) return <div className="container-narrow py-10 text-red">{error}</div>
  if (!spots || !meta || !registrations) return <div className="container-narrow py-10 text-muted">Laden…</div>

  const knownIds = new Set(spots.map((s) => s.id))
  const orphans = registrations.flatMap((r) =>
    r.spots.filter((id) => !knownIds.has(id)).map((id) => ({ naam: r.naam, missingId: id })),
  )

  const filtered = filter
    ? spots.filter((s) =>
        [s.id, s.label, s.zone, s.streetAddress].some((v) => v.toLowerCase().includes(filter.toLowerCase())),
      )
    : spots

  const sorted = [...filtered].sort((a, b) => a.zone.localeCompare(b.zone) || a.id.localeCompare(b.id))

  function startDraw(prefill?: SpotProps) {
    setError(null)
    setDrawPrefill(prefill ?? null)
    setMode({ kind: 'draw', vertices: [] })
  }
  function startEdit(spotId: string) {
    const s = spots!.find((x) => x.id === spotId)
    if (!s) return
    const ring = s.coordinates.slice()
    if (ring.length > 1) {
      const [fLng, fLat] = ring[0]
      const [lLng, lLat] = ring[ring.length - 1]
      if (fLng === lLng && fLat === lLat) ring.pop()
    }
    setError(null)
    setMode({ kind: 'edit', spotId, vertices: ring as [number, number][] })
  }
  function cancel() { setError(null); setDrawPrefill(null); setMode({ kind: 'view' }) }

  function initDraft(vertices: [number, number][]) {
    if (mode.kind !== 'draw' || mode.vertices.length > 0) return
    setMode({ kind: 'draw', vertices })
  }
  function moveDraftVertex(idx: number, lng: number, lat: number) {
    if (mode.kind !== 'draw') return
    const next = mode.vertices.slice()
    next[idx] = [lng, lat]
    setMode({ kind: 'draw', vertices: next })
  }
  function moveVertex(idx: number, lng: number, lat: number) {
    if (mode.kind !== 'edit') return
    const next = mode.vertices.slice()
    next[idx] = [lng, lat]
    setMode({ ...mode, vertices: next })
  }
  function setDraftVertices(vertices: [number, number][]) {
    if (mode.kind !== 'draw') return
    setMode({ kind: 'draw', vertices })
  }
  function setEditVertices(vertices: [number, number][]) {
    if (mode.kind !== 'edit') return
    setMode({ ...mode, vertices })
  }

  async function saveCreate(props: SpotProps) {
    if (mode.kind !== 'draw') return
    if (mode.vertices.length < 3) { setError('Klik minstens 3 punten op de kaart.'); return }
    setBusy(true)
    const res = await api<{ spot: PublicSpot }>(
      '/api/admin/spots/add',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...props, coordinates: mode.vertices }),
      },
    )
    setBusy(false)
    if (!res.ok) {
      if (res.error === 'duplicate-id') setError(`Plek-id "${props.id}" bestaat al.`)
      else if (res.errors) setError(Object.values(res.errors).join(' · '))
      else setError(foutTekst(res.error, 'Opslaan mislukt.'))
      return
    }
    setError(null)
    setDrawPrefill(null)
    setMode({ kind: 'view' })
    load()
  }

  async function saveEdit(props: SpotProps) {
    if (mode.kind !== 'edit') return
    setBusy(true)
    const res = await api<{ spot: PublicSpot; cascadedClaims: number }>(
      `/api/admin/spots/${encodeURIComponent(mode.spotId)}`,
      {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...props, coordinates: mode.vertices }),
      },
    )
    setBusy(false)
    if (!res.ok) {
      if (res.error === 'duplicate-id') setError(`Plek-id "${props.id}" bestaat al.`)
      else setError(foutTekst(res.error, 'Opslaan mislukt.'))
      return
    }
    setError(null)
    if (res.data.cascadedClaims > 0) {
      alert(`${res.data.cascadedClaims} claim(s) bijgewerkt naar de nieuwe plek-id.`)
    }
    setMode({ kind: 'view' })
    load()
  }

  async function doDelete() {
    if (mode.kind !== 'edit') return
    setBusy(true)
    const res = await api(
      `/api/admin/spots/${encodeURIComponent(mode.spotId)}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      },
    )
    setBusy(false)
    if (!res.ok) {
      if (res.error === 'claimed') setError('Kan niet verwijderen — polygoon is geclaimd door een inschrijving.')
      else setError(foutTekst(res.error, 'Verwijderen mislukt.'))
      return
    }
    setError(null)
    setMode({ kind: 'view' })
    load()
  }

  const selectedSpot = mode.kind === 'edit' ? spots.find((s) => s.id === mode.spotId) : undefined
  const initialFormProps: SpotProps = selectedSpot
    ? { id: selectedSpot.id, label: selectedSpot.label, zone: selectedSpot.zone, streetAddress: selectedSpot.streetAddress }
    : (mode.kind === 'draw' && drawPrefill)
      ? drawPrefill
      : { id: '', label: '', zone: '', streetAddress: '' }

  // Spots from last year that still need a polygon (not yet drawn).
  const pendingToDraw = (PENDING_SPOTS as SpotProps[]).filter((p) => !knownIds.has(p.id))

  return (
    <div className="mx-auto max-w-7xl px-4 py-4 h-screen flex flex-col">
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-baseline gap-3">
          <Link to="/beheer" className="text-sm underline text-muted">← Terug naar Beheer</Link>
          <h1 className="font-display text-3xl tracking-display uppercase">Plekken</h1>
          <span className="text-sm text-muted">{spots.length} polygonen</span>
        </div>
        <button onClick={onLogout} className="text-sm text-muted underline">Afmelden</button>
      </div>

      {orphans.length > 0 && (
        <div className="mb-3 border-[3px] border-red bg-red/10 px-4 py-2 text-sm">
          ⚠ <strong>{orphans.length} wees-claim(s)</strong> — inschrijving verwijst naar onbekende plek-id:
          {' '}
          {orphans.slice(0, 5).map((o) => `${o.naam} → ${o.missingId}`).join(', ')}
          {orphans.length > 5 ? `, +${orphans.length - 5} meer` : ''}.
          {' '}Los op via <Link to="/beheer" className="underline">Beheer</Link> (manueel reassign).
        </div>
      )}

      <div className="flex flex-1 gap-4 min-h-0">
        <aside className="w-72 flex flex-col gap-3 min-h-0">
          <input
            className="input"
            placeholder="Zoek id, adres, zone…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button
            type="button"
            onClick={() => startDraw()}
            className="px-4 py-2 bg-ink text-lime font-display tracking-display uppercase border-[3px] border-ink shadow-block-sm hover:translate-y-[1px]"
          >
            + Nieuw plekje
          </button>

          {pendingToDraw.length > 0 && (
            <div className="border-[3px] border-ink bg-lime/30">
              <div className="px-3 py-2 font-display tracking-display uppercase text-sm border-b border-ink bg-lime">
                Nog te tekenen · {pendingToDraw.length}
              </div>
              <ul className="max-h-48 overflow-auto">
                {pendingToDraw.map((p) => (
                  <li
                    key={p.id}
                    onClick={() => startDraw(p)}
                    title="Klik om deze plek te tekenen"
                    className="px-3 py-2 cursor-pointer border-b border-line text-sm hover:bg-lime/60"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-display tracking-display">{p.id}</span>
                      <span className="text-xs">✏️</span>
                    </div>
                    <div className="text-xs text-muted">{p.streetAddress}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ul className="flex-1 overflow-auto border-[3px] border-ink bg-offwhite">
            {sorted.map((s) => {
              const claimedBy = claims.get(s.id) ?? []
              const isSelected = mode.kind === 'edit' && mode.spotId === s.id
              return (
                <li
                  key={s.id}
                  onClick={() => startEdit(s.id)}
                  className={`px-3 py-2 cursor-pointer border-b border-line text-sm ${isSelected ? 'bg-ink text-lime' : 'hover:bg-lime/30'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display tracking-display">{s.id}</span>
                    {claimedBy.length > 0 && (
                      <span title={claimedBy.map((r) => r.naam).join(', ')} className="text-xs">✓</span>
                    )}
                  </div>
                  <div className={`text-xs ${isSelected ? 'text-lime/80' : 'text-muted'}`}>{s.streetAddress}</div>
                </li>
              )
            })}
          </ul>
        </aside>

        <main className="flex-1 flex flex-col gap-3 min-h-0">
          <MapEditor
            spots={spots}
            meta={meta}
            mode={mode.kind}
            selectedSpotId={mode.kind === 'edit' ? mode.spotId : null}
            draftVertices={mode.kind === 'draw' ? mode.vertices : []}
            editVertices={mode.kind === 'edit' ? mode.vertices : undefined}
            onInitDraft={initDraft}
            onMoveDraftVertex={moveDraftVertex}
            onMoveVertex={moveVertex}
            onSetDraftVertices={setDraftVertices}
            onSetEditVertices={setEditVertices}
            onSelect={(id) => startEdit(id)}
            fill
          />
          {mode.kind === 'draw' && (
            <div className="text-sm text-muted">
              De plek blijft een rechthoek. Sleep een hoek om ze breder of langer
              te maken, sleep het midden om ze te verplaatsen, of gebruik de paarse
              knop om ze te draaien.
            </div>
          )}
          {(mode.kind === 'draw' || mode.kind === 'edit') && (
            <SpotPropsForm
              key={mode.kind === 'edit' ? mode.spotId : (drawPrefill?.id ?? 'new')}
              initial={initialFormProps}
              mode={mode.kind === 'draw' ? 'create' : 'edit'}
              busy={busy}
              occupants={mode.kind === 'edit' ? claims.get(mode.spotId) : undefined}
              error={error}
              onSave={mode.kind === 'draw' ? saveCreate : saveEdit}
              onCancel={cancel}
              onDelete={mode.kind === 'edit' ? doDelete : undefined}
            />
          )}
        </main>
      </div>
    </div>
  )
}
